/**
 * Reusable JSON-LD schema generators for SEO
 */

const SITE_URL = 'https://traderadarglobal.com';
const SITE_NAME = 'TradeRadar';

/**
 * Organization schema (site-wide)
 */
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  sameAs: [
    // Add social media links if available
    // 'https://twitter.com/traderadar',
    // 'https://linkedin.com/company/traderadar',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    email: 'contact@traderadarglobal.com',
    availableLanguage: ['English'],
  },
});

/**
 * WebSite schema with search action
 */
export const getWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/countries?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

/**
 * Breadcrumb schema
 */
export const getBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url || `${SITE_URL}${item.path}`,
  })),
});

/**
 * Dataset schema for statistics/data pages
 */
export const getDatasetSchema = (name, description, data) => {
  const datePublished = data.datePublished || new Date().toISOString();
  const dateModified = data.dateModified || datePublished;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name,
    description,
    url: `${SITE_URL}${data.path || ''}`,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    creator: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    license: data.license || `${SITE_URL}/license`,
    datePublished,
    dateModified,
    keywords: Array.isArray(data.keywords) ? data.keywords : (data.keywords ? [data.keywords] : []),
    ...(data.distribution && Array.isArray(data.distribution) && data.distribution.length > 0 && { distribution: data.distribution }),
    ...(data.measurementTechnique && { measurementTechnique: data.measurementTechnique }),
    ...(data.variableMeasured && { variableMeasured: data.variableMeasured }),
  };
};

/**
 * Schema.org Thing for historical timeline episodes (not Event — avoids duplicate/incomplete
 * Event entities in Google Search Console for past incidents).
 */
export const getTimelineEpisodeThingSchema = (event) => {
  const date = event.event_date || event.created_at || event.date || event.startDate;
  const description =
    event.summary || event.description || event.content || '';
  return {
    '@type': 'Thing',
    name: event.title || event.name || 'Historical episode',
    ...(description && { description: String(description).slice(0, 5000) }),
    ...(date && { temporalCoverage: date }),
  };
};

/**
 * Event schema for timeline events
 */
export const getEventSchema = (event) => {
  const startDate =
    event.startDate || event.event_date || event.created_at || event.date;
  let endDate = event.endDate;
  
  // Calculate endDate if not provided (default to 1 day after startDate for single-day events)
  if (!endDate && startDate) {
    try {
      const start = new Date(startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      endDate = end.toISOString();
    } catch (e) {
      // If date parsing fails, use startDate as endDate
      endDate = startDate;
    }
  }

  // Ensure endDate is always set
  if (!endDate && startDate) {
    endDate = startDate;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title || event.name || 'Historical Event',
    description: event.summary || event.description || event.content || '',
    startDate: startDate || new Date().toISOString(),
    endDate: endDate || startDate || new Date().toISOString(),
    location: {
      '@type': 'Place',
      name: event.locationName || 'Strait of Hormuz',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Strait of Hormuz',
        addressRegion: event.locationRegion || 'Hormozgan Province',
        addressCountry: event.locationCountry || 'IR',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: event.latitude || 26.5,
        longitude: event.longitude || 56.5,
      },
    },
    image: event.image || `${SITE_URL}/api/og-image/hormuz-timeline.png`,
    eventStatus: event.eventStatus || 'https://schema.org/EventScheduled',
    organizer: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: event.url || `${SITE_URL}/strait-of-hormuz/historical-timeline`,
      validFrom: startDate || new Date().toISOString(),
    },
    ...(event.url && { url: event.url }),
  };
};

/**
 * HowTo schema for calculator/tutorial pages
 */
export const getHowToSchema = (name, description, steps) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name,
  description,
  step: steps.map((step, index) => ({
    '@type': 'HowToStep',
    position: index + 1,
    name: step.name,
    text: step.text,
    ...(step.image && { image: step.image }),
  })),
});

/**
 * MonitoringService schema for risk dashboard
 */
export const getMonitoringServiceSchema = (name, description, url) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'MonitoringService',
  name,
  description,
  provider: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
  },
  areaServed: {
    '@type': 'Place',
    name: 'Strait of Hormuz',
  },
  url,
});

/**
 * CollectionPage schema for list/news pages
 */
export const getCollectionPageSchema = (name, description, items) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name,
  description,
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: item['@type']
        ? item
        : {
            '@type': item.type || 'Thing',
            ...item,
          },
    })),
  },
});

/**
 * NewsArticle schema
 */
export const getNewsArticleSchema = (article) => ({
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  headline: article.headline || article.title,
  description: article.description || article.ai_summary || article.summary,
  datePublished: article.published_at || article.datePublished,
  dateModified: article.updated_at || article.dateModified,
  author: {
    '@type': 'Organization',
    name: article.source || SITE_NAME,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
    },
  },
  ...(article.url && { url: article.url }),
  ...(article.image && { image: article.image }),
});

/**
 * Article schema
 */
export const getArticleSchema = (article) => {
  const author =
    article.authorPerson && article.authorPerson.name
      ? {
          '@type': 'Person',
          name: article.authorPerson.name,
          ...(article.authorPerson.jobTitle && { jobTitle: article.authorPerson.jobTitle }),
        }
      : {
          '@type': 'Organization',
          name: SITE_NAME,
        };

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.seo_title || article.title || article.headline,
    description: article.seo_description || article.description,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': article.url || `${SITE_URL}${article.path}`,
    },
    datePublished: article.created_at || article.datePublished,
    dateModified: article.updated_at || article.dateModified,
    author,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo.png`,
      },
    },
    ...(article.image && { image: article.image }),
  };
};

/**
 * FAQ schema
 */
export const getFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.title || faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer || faq.content || '',
    },
  })),
});

